import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { toast } from 'react-toastify';
import keyIcon from '../assets/password/key.png';
import PasswordInput from '../components/PasswordInput';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  // Check if we have a token in the URL
  const token = searchParams.get('token');
  const isResetMode = !!token;

  const handleBackToLogin = () => {
    navigate('/auth');
  };

  const handleRequestReset = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    withTokenRefresh(async () => {
      try {
        await apiClient.requestPasswordReset(email);
        // Show success message
        toast.success(
          'If the email is valid, password reset instructions have been sent to your email.'
        );
        setEmailSent(true);
      } catch (err) {
        setError('Failed to send reset instructions. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  // Simple password validation function
  const validatePassword = (password: string): boolean => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password)
    );
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(
        'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number'
      );
      setIsSubmitting(false);
      return;
    }

    withTokenRefresh(async () => {
      try {
        const response = await apiClient.resetPassword({
          token: token!,
          newPassword,
        });
        // Show success message and redirect
        if (response.success) {
          toast.success('Password has been reset successfully.');
          navigate('/auth');
        } else {
          setError(response.message || 'Failed to reset password. Please try again.');
        }
      } catch (err) {
        setError('Failed to reset password. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  const renderEmailForm = () => (
    <form onSubmit={handleRequestReset} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#617BFF]"
          required
        />
      </div>

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2 px-4 bg-[#617BFF] text-white rounded-md hover:bg-[#4a62cc] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#617BFF] transition-colors disabled:opacity-50"
      >
        {isSubmitting ? 'Sending...' : 'Reset password'}
      </button>
    </form>
  );

  const renderPasswordResetForm = () => (
    <form onSubmit={handleResetPassword} className="space-y-4">
      <PasswordInput
        password={newPassword}
        passwordConfirm={confirmPassword}
        onPasswordChange={setNewPassword}
        onPasswordConfirmChange={setConfirmPassword}
        showValidation={true}
      />

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-2 px-4 bg-[#617BFF] text-white rounded-md hover:bg-[#4a62cc] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#617BFF] transition-colors disabled:opacity-50"
      >
        {isSubmitting ? 'Resetting...' : 'Reset password'}
      </button>
    </form>
  );

  const renderSuccessMessage = () => (
    <div className="text-center">
      <h2 className="text-2xl font-medium text-green-600 mb-4">
        Check your email
      </h2>
      <div className="flex justify-center mb-4">
        <svg
          className="w-12 h-12 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <p className="text-gray-600 mb-4">
        If you provided a valid email address of a Flashback account, we have sent password reset instructions.
      </p>
      <p className="text-sm text-gray-500 mb-6">
        Please check your inbox and follow the instructions to reset your password.
      </p>
      <button
        onClick={handleBackToLogin}
        className="w-full py-2 px-4 bg-[#617BFF] text-white rounded-md hover:bg-[#4a62cc] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#617BFF] transition-colors"
      >
        Back to log in
      </button>
    </div>
  );

  // If email was sent successfully, show success message
  if (emailSent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
        <div className="w-full max-w-[360px] mx-auto">
          <div className="flex justify-center mb-6">
            <div className="w-[56px] h-[56px] rounded-[12px] border border-gray-200 flex items-center justify-center bg-white overflow-hidden">
              <img src={keyIcon} alt="Key icon" className="w-8 h-8" />
            </div>
          </div>
          {renderSuccessMessage()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
      <div className="w-full max-w-[360px] mx-auto">
        <div className="flex justify-center mb-6">
          <div className="w-[56px] h-[56px] rounded-[12px] border border-gray-200 flex items-center justify-center bg-white overflow-hidden">
            <img src={keyIcon} alt="Key icon" className="w-8 h-8" />
          </div>
        </div>

        <h1 className="text-xl font-semibold text-center mb-2">
          {isResetMode ? 'Reset your password' : 'Forgot password?'}
        </h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          {isResetMode
            ? 'Enter your new password below.'
            : "No worries, we'll send you reset instructions."}
        </p>

        {isResetMode ? renderPasswordResetForm() : renderEmailForm()}

        <div className="mt-6 text-center">
          <button
            onClick={handleBackToLogin}
            className="inline-flex items-center gap-2 text-sm text-[#667085] hover:text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Back to log in
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
